import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { ArrowLeft, Users, Star } from "lucide-react";
import { PlayerAutocomplete } from "@/components/PlayerAutocomplete";

export default function Players() {
  const navigate = useNavigate();
  const { roundId } = useParams();
  const [loading, setLoading] = useState(false);
  const [round, setRound] = useState(null);
  const [favoritePlayers, setFavoritePlayers] = useState([]);
  const [players, setPlayers] = useState([
    { id: "p1", name: "", handicap: 0, isBase: false },
    { id: "p2", name: "", handicap: 0, isBase: false },
    { id: "p3", name: "", handicap: 0, isBase: false },
    { id: "p4", name: "", handicap: 0, isBase: false },
    { id: "p5", name: "", handicap: 0, isBase: false },
  ]);

  const fetchRound = useCallback(async () => {
    try {
      const response = await fetch(`${API}/rounds/${roundId}`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setRound(data);
        
        // If players already exist, load them
        if (data.players && data.players.length >= 4) {
          setPlayers(data.players.map(p => ({
            id: p.player_id,
            name: p.name,
            handicap: p.handicap,
            isBase: p.is_base,
          })));
        }
      } else {
        toast.error("Ronda no encontrada");
        navigate("/dashboard");
      }
    } catch {
      toast.error("Error al cargar la ronda");
    }
  }, [roundId, navigate]);

  const fetchFavoritePlayers = useCallback(async () => {
    try {
      const response = await fetch(`${API}/players/favorites`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setFavoritePlayers(data);
      }
    } catch (err) {
      console.error("Error fetching favorites:", err);
    }
  }, []);

  useEffect(() => {
    fetchRound();
    fetchFavoritePlayers();
  }, [fetchRound, fetchFavoritePlayers]);

  const updatePlayer = (index, field, value) => {
    const newPlayers = [...players];
    newPlayers[index] = { ...newPlayers[index], [field]: value };
    setPlayers(newPlayers);
  };

  const toggleBase = (index) => {
    const currentBaseCount = players.filter(p => p.isBase).length;
    const isCurrentlyBase = players[index].isBase;

    // Can only have 2 base players
    if (!isCurrentlyBase && currentBaseCount >= 2) {
      toast.error("Solo puedes seleccionar 2 jugadores base");
      return;
    }

    updatePlayer(index, "isBase", !isCurrentlyBase);
  };

  const saveToFavorites = async (index) => {
    const player = players[index];
    if (!player.name.trim()) {
      toast.error("El jugador debe tener nombre");
      return;
    }

    try {
      const response = await fetch(`${API}/players/favorites`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          name: player.name.trim(),
          handicap: parseInt(player.handicap) || 0,
        }),
      });

      if (response.ok) {
        const newFavorite = await response.json();
        setFavoritePlayers([...favoritePlayers, newFavorite].sort((a, b) => 
          a.name.localeCompare(b.name)
        ));
        toast.success(`${player.name} agregado a favoritos`);
      } else {
        throw new Error("Error saving favorite");
      }
    } catch {
      
      toast.error("Error al guardar en favoritos");
    }
  };

  const handleSubmit = async () => {
    // Validate all players have names
    const emptyPlayers = players.filter(p => !p.name.trim());
    if (emptyPlayers.length > 0) {
      toast.error("Todos los jugadores deben tener nombre");
      return;
    }

    // Validate exactly 2 base players
    const baseCount = players.filter(p => p.isBase).length;
    if (baseCount !== 2) {
      toast.error("Debes seleccionar exactamente 2 jugadores base");
      return;
    }

    setLoading(true);
    try {
      const playersData = players.map(p => ({
        player_id: p.id,
        name: p.name,
        handicap: parseInt(p.handicap) || 0,
        linked_user_id: p.linked_user_id || undefined,
      }));

      const basePlayerIds = players.filter(p => p.isBase).map(p => p.id);

      const response = await fetch(`${API}/rounds/${roundId}/players`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          players: playersData,
          base_player_ids: basePlayerIds,
        }),
      });

      if (response.ok) {
        toast.success("Jugadores agregados");
        navigate(`/round/${roundId}/preview`);
      } else {
        throw new Error("Error adding players");
      }
    } catch {
      
      toast.error("Error al agregar jugadores");
    } finally {
      setLoading(false);
    }
  };

  const baseCount = players.filter(p => p.isBase).length;
  const secondaryTarget = players.length - 2;
  const secondaryCount = players.length - baseCount;

  const toggleFifthPlayer = () => {
    if (players.length === 5) {
      setPlayers(players.slice(0, 4));
    } else {
      setPlayers([...players, { id: "p5", name: "", handicap: 0, isBase: false }]);
    }
  };

  // Get available favorites (not already selected)
  const getAvailableFavorites = (currentIndex) => {
    const selectedNames = players
      .filter((_, i) => i !== currentIndex)
      .map(p => p.name.toLowerCase().trim())
      .filter(n => n);
    
    return favoritePlayers.filter(f => 
      !selectedNames.includes(f.name.toLowerCase().trim())
    );
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-40">
        <div className="max-w-lg mx-auto px-4 py-4 flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate("/dashboard")}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-[#0F172A]">Jugadores</h1>
            {round && <p className="text-xs text-[#64748B]">{round.name}</p>}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate("/players/favorites")}
            className="text-xs"
            data-testid="manage-favorites-btn"
          >
            <Star className="w-4 h-4 mr-1 text-amber-500" />
            Favoritos
          </Button>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-6">
        {/* Selection Status */}
        <Card className="card-default mb-6">
          <CardContent className="p-4">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-4">
                <div className="text-center">
                  <p className={`text-2xl font-bold ${baseCount === 2 ? 'text-[#103B31]' : 'text-[#64748B]'}`}>
                    {baseCount}/2
                  </p>
                  <p className="text-xs text-[#64748B]">Base</p>
                </div>
                <div className="w-px h-10 bg-gray-200"></div>
                <div className="text-center">
                  <p className={`text-2xl font-bold ${secondaryCount === secondaryTarget ? 'text-[#E6B733]' : 'text-[#64748B]'}`}>
                    {secondaryCount}/{secondaryTarget}
                  </p>
                  <p className="text-xs text-[#64748B]">Secundarios</p>
                </div>
              </div>
              <Users className="w-8 h-8 text-[#103B31]/30" />
            </div>
          </CardContent>
        </Card>

        {/* Players List */}
        <div className="space-y-4 mb-6">
          {players.map((player, index) => {
            const isInFavorites = favoritePlayers.some(f => 
              f.name.toLowerCase().trim() === player.name.toLowerCase().trim()
            );

            return (
              <Card 
                key={player.id} 
                className={`transition-all ${player.isBase ? 'card-active' : 'card-default'}`}
                data-testid={`player-card-${index}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    {/* Base Checkbox */}
                    <div className="pt-2">
                      <Checkbox
                        id={`base-${index}`}
                        checked={player.isBase}
                        onCheckedChange={() => toggleBase(index)}
                        className="w-5 h-5"
                        data-testid={`base-checkbox-${index}`}
                      />
                    </div>

                    {/* Player Info */}
                    <div className="flex-1 space-y-3">
                      <div>
                        <Label className="text-xs text-[#64748B] mb-1 block">
                          Jugador {index + 1} {player.isBase && <span className="text-[#103B31] font-semibold">(Base)</span>}
                        </Label>
                        
                        <PlayerAutocomplete
                          value={player.name}
                          onChange={(val) => updatePlayer(index, "name", val)}
                          onSelect={(suggestion) => {
                            const newPlayers = [...players];
                            newPlayers[index] = {
                              ...newPlayers[index],
                              name: suggestion.name,
                              handicap: suggestion.handicap != null ? suggestion.handicap : newPlayers[index].handicap,
                              linked_user_id: suggestion.type === "profile" ? suggestion.id : undefined,
                            };
                            setPlayers(newPlayers);
                          }}
                          favorites={getAvailableFavorites(index)}
                          excludeNames={players.filter((_, i) => i !== index).map(p => p.name).filter(Boolean)}
                          placeholder="Buscar por nombre..."
                          testId={`player-name-${index}`}
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex-1">
                          <Label className="text-xs text-[#64748B] mb-1 block">Handicap</Label>
                          <Input
                            type="number"
                            min="0"
                            max="54"
                            value={player.handicap}
                            onChange={(e) => updatePlayer(index, "handicap", e.target.value)}
                            placeholder="0"
                            className="input-default w-24"
                            data-testid={`player-handicap-${index}`}
                          />
                        </div>
                        {player.name.trim() && !isInFavorites && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => saveToFavorites(index)}
                            className="mt-5 text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                            title="Guardar en favoritos"
                            data-testid={`save-favorite-${index}`}
                          >
                            <Star className="w-4 h-4 mr-1" />
                            Guardar
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Toggle 4/5 players */}
        <button onClick={toggleFifthPlayer}
          className="w-full mb-6 py-2.5 rounded-xl border-2 border-dashed border-gray-300 text-sm font-medium text-gray-500 hover:border-[#103B31] hover:text-[#103B31] transition-colors"
          data-testid="toggle-fifth-player-btn">
          {players.length === 5 ? "Jugar con 4 jugadores (quitar jugador 5)" : "+ Agregar 5º jugador"}
        </button>

        {/* Info Box */}
        <div className="bg-[#103B31]/5 rounded-lg p-4 mb-6 border border-[#103B31]/10">
          <p className="text-sm text-[#103B31]">
            <strong>Selecciona 2 jugadores base</strong> que jugarán contra las combinaciones
            de los secundarios ({players.length === 5 ? "3 combinaciones con 5 jugadores" : "1 matchup con 4 jugadores"}).
          </p>
        </div>

        <Button 
          onClick={handleSubmit}
          className="btn-primary w-full py-6"
          disabled={loading || baseCount !== 2}
          data-testid="continue-btn"
        >
          {loading ? (
            <span className="flex items-center gap-2">
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              Guardando...
            </span>
          ) : (
            "Continuar a Cálculos"
          )}
        </Button>
      </main>
    </div>
  );
}
